import { Box, ListItem, Popover, TextField, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/system";
import { ArrowLeft, ArrowRight, ChevronDown, GridView, ListView, TargetSelect } from "@pmag/icons";
import { Autocomplete, type AutocompleteOption, Button, Tab, Tabs, Typography } from "@pmag/ui-components";
import { Routes } from "@src/context/router/RouterContext";
import { type GQLComponentGroupParentGenericFragment, type GQLComponentSubGroupGenericFragment } from "@src/graphql.generated";
import { useGlobalState } from "@src/hooks/useGlobalState";
import { useRouter } from "@src/hooks/useRouter";
import { DownloadButton } from "@src/pages/subGroupDetail/subgroupDetailsListSection/downloadButton/DownloadButton";
import { type FunctionComponent, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import {
    ComponentSelectButton,
    ComponentSelectButtonTitle,
    ContentSwitcher,
    ControlPanelContainer,
    GroupHeaderBox,
    GroupItems,
    IconButtonStyled,
    SmallRowContainer,
    StyledBox,
    TopContainer,
} from "./ControlPanel.styles";

export const dataTestId = {
    unknownError: "sparePartsFinder.subgroupPage.controlPanel.unknownError",
    controlPanelContainer: "sparePartsFinder.subgroupPage.controlPanel.container",
    btnBackToComponentOverview: "sparePartsFinder.subgroupPage.controlPanel.btn.backToComponentTemplates",
    btnDownload: "sparePartsFinder.subgroupPage.controlPanel.btn.downloadPDF",
    btnNextComponentGroup: "sparePartsFinder.subgroupPage.controlPanel.btn.nextComponentGroup",
    btnPreviousComponentGroup: "sparePartsFinder.subgroupPage.controlPanel.btn.previousComponentGroup",
    btnOpenComponentGroupSelect: "sparePartsFinder.subgroupPage.controlPanel.btn.openComponentGroupSelect",
    btnGroupContentSwitcher: "sparePartsFinder.subgroupPage.controlPanel.contentSwitcher",
    componentGroupSelectPopover: "sparePartsFinder.subgroupPage.controlPanel.componentGroupSelect.popover",
    componentGroupSelectField: "sparePartsFinder.subgroupPage.controlPanel.componentGroupSelect.input",
    componentGroupSelectAutocomplete: "sparePartsFinder.subgroupPage.controlPanel.componentGroupSelect.autocomplete",
    componentGroupSelectAutocompleteGroupContainer: "sparePartsFinder.subgroupPage.controlPanel.componentGroupSelect.autocomplete.groupContainer",
    componentGroupSelectAutocompleteGroupTitle: "sparePartsFinder.subgroupPage.controlPanel.componentGroupSelect.autocomplete.groupDivider",
    openExplosionGraphicButton: "sparePartsFinder.subgroupPage.controlPanel.openExplosionGraphicButton",
};

export interface ControlPanelProps {
    engineComponents: GQLComponentGroupParentGenericFragment[];
    frameComponents: GQLComponentGroupParentGenericFragment[];
    modelArticleNumber: string;
    setOpenExplosionGraphic: (value: boolean) => void;
}

type SubGroupWithTypeAndCode = GQLComponentSubGroupGenericFragment & { code: string; type: string };
type AutocompleteComponentGroupListItem = AutocompleteOption & SubGroupWithTypeAndCode;

export const ControlPanel: FunctionComponent<ControlPanelProps> = ({
    modelArticleNumber,
    frameComponents,
    engineComponents,
    setOpenExplosionGraphic,
}) => {
    const intl = useIntl();
    const theme = useTheme();

    const { setRoute } = useRouter();

    const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
    const { componentGroupId, setComponentGroupId } = useGlobalState();

    const [tabValue, setTabValue] = useState<number>(0);

    const frameComponentSubGroups = frameComponents
        .reduce((acc, frameComponentGroup) => {
            const groupsWithType = frameComponentGroup.componentSubGroups.map((component) => ({
                ...component,
                type: intl.formatMessage({ id: "sparePartsFinder.subgroupDetailPage.controlPanel.frame", defaultMessage: "Frame" }),
                code: frameComponentGroup.componentGroup.code,
            }));

            return acc.concat(groupsWithType);
        }, [] as SubGroupWithTypeAndCode[])
        .sort((a, b) => a.componentGroup.code.localeCompare(b.componentGroup.code));

    const engineComponentSubGroups = engineComponents
        .reduce((acc, engineComponentGroup) => {
            const groupsWithType = engineComponentGroup.componentSubGroups.map((component) => ({
                ...component,
                type: intl.formatMessage({ id: "sparePartsFinder.subgroupDetailPage.controlPanel.engine", defaultMessage: "Engine" }),
                code: engineComponentGroup.componentGroup.code,
            }));

            return acc.concat(groupsWithType);
        }, [] as SubGroupWithTypeAndCode[])
        .sort((a, b) => a.componentGroup.code.localeCompare(b.componentGroup.code));

    const componentList = [...frameComponentSubGroups, ...engineComponentSubGroups];

    const index = componentList.findIndex((component) => component.componentGroup.id === componentGroupId);
    const [selectedComponentIndex, setSelectedComponentIndex] = useState(index);
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    const handlePreviousClick = () => {
        const newIndex = Math.max(selectedComponentIndex - 1, 0);
        setSelectedComponentIndex(newIndex);

        if (componentList[newIndex].componentGroup.id !== componentGroupId) {
            setComponentGroupId(componentList[newIndex].componentGroup.id);
        }
    };

    const handleNextClick = () => {
        const newIndex = Math.min(selectedComponentIndex + 1, componentList.length - 1);
        setSelectedComponentIndex(newIndex);

        if (componentList[newIndex].componentGroup.id !== componentGroupId) {
            setComponentGroupId(componentList[newIndex].componentGroup.id);
        }
    };

    if (selectedComponentIndex < 0 && selectedComponentIndex < componentList.length) {
        return (
            <Box data-testid={dataTestId.unknownError}>
                <FormattedMessage id="sparePartsFinder.subgroupDetailPage.controlPanel.error" defaultMessage="An unknown error occurred" />
            </Box>
        );
    }

    const transformedAutocompleteList: AutocompleteComponentGroupListItem[] = componentList.map((component) => ({
        ...component,
        label: `${component.code} - ${component.componentGroup.description}`,
        value: component.componentGroup.id,
        id: component.componentGroup.id,
    }));

    const currentComponentItem = componentList.find((component) => component.componentGroup.id === componentGroupId);
    const fileName = currentComponentItem
        ? `${modelArticleNumber}_${currentComponentItem.componentGroup.description.replace(/[^\w\s]/gi, "").replace(/\s/g, "_")}`
        : `${modelArticleNumber}_${componentGroupId.replaceAll("-", "")}`;
    const locale = intl.locale === "de" ? "de" : "en";
    const downloadUrl = `${process.env.API_URL}/bff/component-sub-groups?id=${componentList[selectedComponentIndex].id}&languageCode=${locale}&articleNumber=${modelArticleNumber}`;

    return (
        <ControlPanelContainer data-testid={dataTestId.controlPanelContainer}>
            <TopContainer>
                <Button
                    size="small"
                    variant="primary"
                    data-testid={dataTestId.btnBackToComponentOverview}
                    icon={<GridView />}
                    onClick={() => setRoute(Routes.ComponentGroupOverview)}
                >
                    <FormattedMessage id="article.sparePartsFinder.undefined" defaultMessage="Overview" />
                </Button>

                <ContentSwitcher data-testid={dataTestId.btnGroupContentSwitcher}>
                    <IconButtonStyled
                        variant="transparent"
                        onClick={handlePreviousClick}
                        disabled={selectedComponentIndex === 0}
                        data-testid={dataTestId.btnPreviousComponentGroup}
                    >
                        <ArrowLeft size="small" />
                    </IconButtonStyled>

                    <ComponentSelectButton
                        size="small"
                        variant="secondary"
                        icon={<ChevronDown size="small" />}
                        onClick={(event) => setAnchorEl(event.currentTarget)}
                        data-testid={dataTestId.btnOpenComponentGroupSelect}
                    >
                        <ComponentSelectButtonTitle variant="body-short-01" noWrap={true}>
                            {`${componentList[selectedComponentIndex].code} - ${componentList[selectedComponentIndex].componentGroup.description}`}
                        </ComponentSelectButtonTitle>
                    </ComponentSelectButton>

                    <Popover
                        id="componentList-popover"
                        open={Boolean(anchorEl)}
                        anchorEl={anchorEl}
                        onClose={() => setAnchorEl(null)}
                        anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "center",
                        }}
                        transformOrigin={{
                            vertical: "top",
                            horizontal: "center",
                        }}
                        PaperProps={{
                            style: {
                                width: 300,
                            },
                        }}
                        data-testid={dataTestId.componentGroupSelectPopover}
                    >
                        <StyledBox>
                            <Autocomplete
                                open={true}
                                disablePortal
                                options={transformedAutocompleteList}
                                groupBy={(component) => component.type}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        autoFocus={true}
                                        placeholder={intl.formatMessage({
                                            id: "sparePartsFinder.subgroupPage.controlPanel.autocomplete.placeholder",
                                            defaultMessage: "Search...",
                                        })}
                                        data-testid={dataTestId.componentGroupSelectField}
                                    />
                                )}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                renderGroup={({ key, group, children }) => (
                                    <Box key={key} data-testid={dataTestId.componentGroupSelectAutocompleteGroupContainer}>
                                        <GroupHeaderBox>
                                            <Typography variant="heading-08" data-testid={dataTestId.componentGroupSelectAutocompleteGroupTitle}>
                                                {group}
                                            </Typography>
                                        </GroupHeaderBox>

                                        <GroupItems>{children}</GroupItems>
                                    </Box>
                                )}
                                renderOption={(option, component) => (
                                    <ListItem {...option}>
                                        <Typography variant="body-short-01">{`${component.code} - ${component.componentGroup.description}`}</Typography>
                                    </ListItem>
                                )}
                                onChange={(_, value) => {
                                    if (value) {
                                        const newIndex = componentList.findIndex((component) => component.componentGroup.id === value.id);

                                        setComponentGroupId(value.id);
                                        setSelectedComponentIndex(newIndex);
                                        setAnchorEl(null);
                                    }
                                }}
                                data-testid={dataTestId.componentGroupSelectAutocomplete}
                            />
                        </StyledBox>
                    </Popover>

                    <IconButtonStyled
                        variant="transparent"
                        onClick={handleNextClick}
                        disabled={selectedComponentIndex === componentList.length - 1}
                        data-testid={dataTestId.btnNextComponentGroup}
                    >
                        <ArrowRight size="small" />
                    </IconButtonStyled>
                </ContentSwitcher>

                {!isSmallScreen && <DownloadButton data-testid={dataTestId.btnDownload} fileName={fileName} url={downloadUrl} />}
            </TopContainer>

            <SmallRowContainer>
                <Tabs
                    value={tabValue}
                    onChange={(_, value) => {
                        setOpenExplosionGraphic(value == 1);
                        setTabValue(value);
                    }}
                    size="small"
                >
                    <Tab
                        icon={<ListView size="small" />}
                        label={<FormattedMessage id="sparePartsFinder.subgroupPage.controlPanel.tabs.listView" defaultMessage="List" />}
                    />

                    <Tab
                        icon={<TargetSelect size="small" />}
                        label={<FormattedMessage id="sparePartsFinder.subgroupPage.controlPanel.tabs.explodedView" defaultMessage="Exploded view" />}
                    />
                </Tabs>

                <DownloadButton data-testid={dataTestId.btnDownload} fileName={fileName} url={downloadUrl} />
            </SmallRowContainer>
        </ControlPanelContainer>
    );
};
